import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, Building2, GraduationCap, CheckCircle2 } from 'lucide-react';
import { FakeDoorModal } from '../components/FakeDoorModal';
import { supabase } from '../lib/supabaseClient';

export const LandingPage: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalSource, setModalSource] = useState<'demo' | 'early_access' | null>(null);
    const navigate = useNavigate();

    const openModal = (source: 'demo' | 'early_access') => {
        setModalSource(source);
        setIsModalOpen(true);
    };

    const handleModalSuccess = () => {
        if (modalSource === 'demo') {
            navigate('/demo');
        }
    };

    useEffect(() => {
        // Track page view
        const trackPageView = async () => {
            try {
                // 1. Get IP
                const ipRes = await fetch('https://api.ipify.org?format=json');
                const ipData = await ipRes.json();
                const ip = ipData.ip;

                // 2. Check last visit
                const { data: lastVisits, error: fetchError } = await supabase
                    .from('page_views')
                    .select('visited_at')
                    .eq('origin_ip', ip)
                    .order('visited_at', { ascending: false })
                    .limit(1);

                if (fetchError) {
                    console.error('Error checking last visit:', fetchError);
                }

                let shouldRecord = true;

                if (lastVisits && lastVisits.length > 0) {
                    const lastVisitTime = new Date(lastVisits[0].visited_at).getTime();
                    const now = new Date().getTime();
                    const diffMinutes = (now - lastVisitTime) / 1000 / 60;

                    if (diffMinutes < 3) {
                        shouldRecord = false;
                        console.log('Visit not recorded: less than 3 minutes since last visit.');
                    }
                }

                if (shouldRecord) {
                    await supabase.from('page_views').insert([{ page_path: '/', origin_ip: ip }]);
                }
            } catch (error) {
                console.error('Error tracking page view:', error);
            }
        };

        trackPageView();
    }, []);

    return (
        <div className="min-h-screen bg-white font-sans text-gray-900">
            {/* Navbar Placeholder */}
            <nav className="border-b border-gray-100 bg-white py-4">
                <div className="container mx-auto flex items-center justify-between px-4">
                    <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 font-bold text-white">
                            C
                        </div>
                        <span className="text-xl font-bold text-gray-900">CIDETIC</span>
                    </div>
                    <button
                        onClick={() => openModal('demo')}
                        className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200"
                    >
                        Acceso Demo
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative overflow-hidden py-20 lg:py-32">
                <div className="container mx-auto px-4 text-center">
                    <div className="mx-auto max-w-4xl">
                        <h1 className="mb-6 text-5xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                            Centraliza la <span className="text-blue-600">Transferencia Tecnológica</span>
                        </h1>
                        <p className="mb-10 text-xl text-gray-600">
                            Gestiona patentes, contratos y spin-offs en una sola plataforma.
                            Conecta investigadores con la industria y acelera la innovación sin burocracia.
                        </p>
                        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                            <button
                                onClick={() => openModal('early_access')}
                                className="group flex items-center gap-2 rounded-full bg-blue-600 px-8 py-4 text-lg font-bold text-white shadow-lg transition-all hover:bg-blue-700 hover:shadow-xl"
                            >
                                Solicitar Acceso Anticipado
                                <ArrowRight className="transition-transform group-hover:translate-x-1" />
                            </button>
                            <p className="text-sm text-gray-500">Prueba gratuita de 3 meses • Sin tarjeta de crédito</p>
                        </div>
                    </div>
                </div>

                {/* Background decoration */}
                <div className="absolute left-1/2 top-0 -z-10 h-[1000px] w-[1000px] -translate-x-1/2 rounded-full bg-blue-50 opacity-50 blur-3xl"></div>
            </section>

            {/* Roles Section */}
            <section className="bg-gray-50 py-20">
                <div className="container mx-auto px-4">
                    <div className="mb-16 text-center">
                        <h2 className="text-3xl font-bold text-gray-900">Diseñado para todo el ecosistema</h2>
                        <p className="mt-4 text-lg text-gray-600">Una solución unificada para cada actor clave</p>
                    </div>

                    <div className="grid gap-8 md:grid-cols-3">
                        {/* Card 1: Gestores */}
                        <div className="rounded-2xl bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
                            <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                                <Building2 size={24} />
                            </div>
                            <h3 className="mb-3 text-xl font-bold text-gray-900">Para Gestores TTO</h3>
                            <p className="mb-6 text-gray-600">
                                Automatiza el seguimiento de contratos y licencias. Olvida las hojas de cálculo dispersas y obtén reportes en tiempo real.
                            </p>
                            <ul className="space-y-2 text-sm text-gray-500">
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-blue-500" /> Dashboard centralizado
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-blue-500" /> Alertas de vencimientos
                                </li>
                            </ul>
                        </div>

                        {/* Card 2: Investigadores */}
                        <div className="rounded-2xl bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
                            <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-green-100 text-green-600">
                                <GraduationCap size={24} />
                            </div>
                            <h3 className="mb-3 text-xl font-bold text-gray-900">Para Investigadores</h3>
                            <p className="mb-6 text-gray-600">
                                Registra tus invenciones fácilmente y sigue el estado de protección y comercialización sin correos interminables.
                            </p>
                            <ul className="space-y-2 text-sm text-gray-500">
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-green-500" /> Registro simplificado
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-green-500" /> Transparencia total
                                </li>
                            </ul>
                        </div>

                        {/* Card 3: Industria */}
                        <div className="rounded-2xl bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
                            <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100 text-purple-600">
                                <Users size={24} />
                            </div>
                            <h3 className="mb-3 text-xl font-bold text-gray-900">Para Industria</h3>
                            <p className="mb-6 text-gray-600">
                                Accede al portafolio tecnológico de la universidad y conecta con los grupos de investigación líderes.
                            </p>
                            <ul className="space-y-2 text-sm text-gray-500">
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-purple-500" /> Catálogo de patentes
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-purple-500" /> Conexión directa
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-100 bg-white py-12">
                <div className="container mx-auto px-4 text-center text-gray-500">
                    <p>&copy; {new Date().getFullYear()} CIDETIC. Todos los derechos reservados.</p>
                </div>
            </footer>

            {/* Fake Door Modal */}
            <FakeDoorModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};
